/**
 * Formalização preparada para revisão (E152.F3, GUS-175, ADR-0103 §5.9), na mesa eletrônica.
 *
 * Uma ação principal: a revisão fundamentada dos pontos que pedem o perito. A conferência da
 * análise, a conclusão e a proposta vêm primeiro; as revisões registradas ficam à vista, com a
 * opção de rever a decisão em vigor; a análise completa abre por expansão. Registrar renova a
 * análise na hora; nada aqui assina, publica ou decide no lugar do servidor.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';
import { Link } from 'react-router-dom';

import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { PainelDaFormalizacaoData, RevisaoEmVigorData } from '@/lib/formalizacaoAvaliacao';
import { useParecerFacial } from '@/lib/useParecerFacial';
import { useAvaliacaoDaFormalizacao, type EnvioDaRevisao } from '@/lib/useAvaliacaoDaFormalizacao';
import { NoDocumento } from '@/components/MesaUnica/NoDocumento';

import { AnaliseCompleta } from './AnaliseCompleta';
import { PontosParaRevisao } from './PontosParaRevisao';
import { RevisoesRegistradas } from './RevisoesRegistradas';

const t = ptBR.formalizacaoAvaliacao;
const tn = ptBR.mesaUnica.noDocumento;
/** E153.F3: o link para a página do resultado auditado, do mesmo caso. */
const verResultado = ptBR.resultadoAuditado.verResultado;

function AvisoDoEnvio({ envio }: { envio: EnvioDaRevisao }): JSX.Element | null {
  if (envio.estado === 'registrada') {
    return (
      <p role="status" className="text-body-sm text-success-700" data-testid="revisao-registrada">
        {t.formulario.registrada}
      </p>
    );
  }
  if (envio.estado === 'recusada') {
    return (
      <p role="alert" className="text-body-sm text-danger-700" data-testid="revisao-recusada">
        {t.recusa[envio.motivo]}
      </p>
    );
  }
  return null;
}

interface ProntoProps {
  painel: PainelDaFormalizacaoData;
  envio: EnvioDaRevisao;
  revisar: ReturnType<typeof useAvaliacaoDaFormalizacao>['revisar'];
  client: AxiosInstance;
  caseId: string;
  noDocumento: NoDocumentoDaFormalizacao | undefined;
}

/** E203.F5.T09: na mesa, os pontos se revisam pelo documento; aqui ficam o resumo e o aviso. */
export interface NoDocumentoDaFormalizacao {
  ancora: string | null;
}

function resumoDasRevisoes(pendentes: number): string {
  if (pendentes === 0) return tn.semRevisaoPendente;
  if (pendentes === 1) return tn.umaRevisaoPendente;
  return tn.revisoesPendentes.replace('{n}', String(pendentes));
}

function Pronto({ painel, envio, revisar, client, caseId, noDocumento }: ProntoProps): JSX.Element {
  const [rever, setRever] = useState<RevisaoEmVigorData | null>(null);
  const gravada = painel.avaliacao;
  const parecerFacial = useParecerFacial(client, caseId, gravada.versao); // E172.F4 (B2.3)
  const enviando = envio.estado === 'enviando' ? envio.objeto : null;
  async function registrar(...args: Parameters<typeof revisar>): Promise<boolean> {
    const aceita = await revisar(...args);
    if (aceita) setRever(null);
    return aceita;
  }
  const conteudo = (
    <>
      <AvisoDoEnvio envio={envio} />
      <PontosParaRevisao
        avaliacao={gravada.conteudo.avaliacao}
        versao={gravada.versao}
        rever={rever}
        enviando={enviando}
        onRegistrar={registrar}
        onCancelarRever={() => setRever(null)}
        parecerFacial={parecerFacial}
      />
      <RevisoesRegistradas painel={painel} onRever={setRever} />
      <AnaliseCompleta resultado={gravada.conteudo} />
    </>
  );
  if (noDocumento === undefined) return conteudo;
  const pendentes = gravada.conteudo.avaliacao.revisoes_pendentes.length;
  return (
    <NoDocumento
      id="revisoes"
      resumo={resumoDasRevisoes(pendentes)}
      pendente={pendentes > 0}
      ancora={noDocumento.ancora}
    >
      {conteudo}
    </NoDocumento>
  );
}

export interface FormalizacaoPreparadaProps {
  client: AxiosInstance;
  caseId: string;
  /** Muda quando o caso é relido (exame verificado de novo): a análise é lida de novo. */
  releitura?: unknown;
  /** E203.F5.T09: na mesa, o resumo e o aviso que leva ao documento; sem ele, o card inteiro. */
  noDocumento?: NoDocumentoDaFormalizacao;
}

/** O título e "Ver resultado"; na mesa, numa linha só. */
function Titulo({ caseId, naMesa }: { caseId: string; naMesa: boolean }): JSX.Element {
  return (
    <div className={naMesa ? 'flex flex-wrap items-baseline gap-x-sc-3 gap-y-1' : undefined}>
      <h3
        id="formalizacao-preparada-titulo"
        className={naMesa ? 'text-body-md font-semibold text-fg' : 'text-body-lg text-fg'}
      >
        {t.titulo}
      </h3>
      <Link
        className={
          naMesa
            ? 'text-body-sm text-accent-700'
            : 'mt-sc-2 inline-block text-body-sm text-accent-700'
        }
        to={`/casos/${caseId}/resultado`}
        data-testid="formalizacao-ver-resultado"
      >
        {verResultado}
      </Link>
    </div>
  );
}

function Erro({
  motivo,
  onTentarDeNovo,
}: {
  motivo: 'sem_permissao' | 'nao_encontrado' | string;
  onTentarDeNovo: () => void;
}): JSX.Element {
  const definitivo = motivo === 'sem_permissao' || motivo === 'nao_encontrado';
  const textos: Record<string, string> = {
    sem_permissao: t.semPermissao,
    nao_encontrado: t.naoEncontrado,
  };
  return (
    <div role="alert" className="space-y-sc-2" data-testid="formalizacao-preparada-erro">
      <p className="text-body-sm text-danger-700">{textos[motivo] ?? t.erro}</p>
      {definitivo ? null : (
        <Button type="button" variant="secondary" size="sm" onClick={onTentarDeNovo}>
          {t.tentarDeNovo}
        </Button>
      )}
    </div>
  );
}

export function FormalizacaoPreparada({
  client,
  caseId,
  releitura,
  noDocumento,
}: FormalizacaoPreparadaProps): JSX.Element {
  const { avaliacao, envio, reler, revisar } = useAvaliacaoDaFormalizacao(
    client,
    caseId,
    releitura,
  );
  // E205.F5.T02 (ADR-0147 §2.7): na mesa, sem a caixa por fora — o título numa linha com "Ver
  // resultado" e o quadro do que se resolve (`NoDocumento`) como a única caixa.
  const naMesa = noDocumento !== undefined;
  const corpo = (
    <>
      <Titulo caseId={caseId} naMesa={naMesa} />
      {avaliacao.estado === 'carregando' ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : null}
      {avaliacao.estado === 'erro' ? (
        <Erro motivo={avaliacao.motivo} onTentarDeNovo={reler} />
      ) : null}
      {avaliacao.estado === 'pronto' ? (
        <Pronto
          painel={avaliacao.dados}
          envio={envio}
          revisar={revisar}
          client={client}
          caseId={caseId}
          noDocumento={noDocumento}
        />
      ) : null}
    </>
  );
  const atributos = {
    'data-testid': 'formalizacao-preparada',
    'data-estado': avaliacao.estado,
    'data-na-mesa': naMesa,
    'aria-labelledby': 'formalizacao-preparada-titulo',
  };
  if (naMesa) {
    return (
      <section className="mt-sc-6 space-y-sc-3" {...atributos}>
        {corpo}
      </section>
    );
  }
  return (
    <Blueprint as="section" className="mt-sc-6 space-y-sc-4 p-sc-6" {...atributos}>
      {corpo}
    </Blueprint>
  );
}
